import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  NotebookPen,
  ClipboardList,
  ClipboardCheck,
  FileBarChart2,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Upload,
  Tags,
  CalendarClock,
  ChevronRight,
} from 'lucide-react';

/**
 * NotesDocsLanding — Phase 1 (reduced scope, 10 cards).
 *
 * Cards in mandated order:
 *   1. Progress Notes                → sub=notes           (reuse existing NotesTab)
 *   2. Care Plans                    → LINK-OUT to Care & Support (?tab=care-support&sub=careplans)
 *   3. Assessments                   → sub=assessments     (unified read-INDEX only)
 *   4. Reports                       → sub=reports         (combined labelled index)
 *   5. Incident Forms                → LINK-OUT to /compliance/incidents?participantId=<pid>
 *   6. Consent Forms                 → sub=consents        (reuse existing ConsentsTab)
 *   7. Risk Assessments              → LINK-OUT to ?tab=risk&sub=risks
 *   8. Upload Documents & Forms      → sub=documents       (reuse existing ParticipantDocumentsTab)
 *   9. Document Categories           → sub=document-categories (read-only reference)
 *  10. Review & Expiry Dates         → sub=expiring        (reuse existing ExpiringItemsTab)
 *
 * DEFERRED and ABSENT from DOM (must not render under any circumstance):
 *   - Case Management Notes
 *   - Handover Notes
 *   - Version History
 *
 * Feature flag: participant_notesdocs_landing_enabled (default OFF; runtime ON only in
 * non-live env). Controls navigation visibility ONLY. Every destination inherits its
 * existing RBAC.
 */

export function NotesDocsLanding({ participantId, onOpenLeaf, onOpenParentTab }) {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);
    api.get(`/participants/${participantId}/overview`)
      .then((r) => { if (!cancelled) setOverview(r?.data?.data || null); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) setDenied(true);
        else setError('Unable to load Notes & Docs summary.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const activePlanTitle = useMemo(() => {
    const p = overview?.activePlan;
    if (p && typeof p === 'object') return p.title || p.name || null;
    return null;
  }, [overview]);

  if (denied) {
    return (
      <div
        className="p-6 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
        data-testid="notesdocs-landing-denied"
      >
        You do not have permission to view Notes &amp; Docs for this participant.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="notesdocs-landing-loading">
        {Array.from({ length: 10 }).map((_, i) => (
          <Card key={i} className="h-32 animate-pulse bg-slate-100/60" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="p-6 text-sm text-red-600"
        role="alert"
        aria-live="assertive"
        data-testid="notesdocs-landing-error"
      >
        {error}
      </div>
    );
  }

  // Cross-tab / external navigation helpers (link-out cards)
  const openParent = (tabId, subId) => {
    if (onOpenParentTab) onOpenParentTab(tabId, subId);
    else {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tabId);
      if (subId) url.searchParams.set('sub', subId);
      navigate(url.pathname + '?' + url.searchParams.toString());
    }
  };
  const openIncidentsExternal = () => {
    navigate(`/compliance/incidents?participantId=${participantId}`);
  };

  // 10 cards in mandated order. Deferred items (Case Management, Handover, Version History)
  // are intentionally OMITTED from this array — they must not appear in the DOM.
  const cards = [
    {
      id: 'notes',
      testId: 'notesdocs-card-progress-notes',
      title: 'Progress Notes',
      icon: NotebookPen,
      description: 'Participant-filtered progress notes (read-only from Notes & Docs).',
      onClick: () => onOpenLeaf('notes'),
      actionLabel: 'Open Progress Notes',
    },
    {
      id: 'care-plans',
      testId: 'notesdocs-card-care-plans',
      title: 'Care Plans',
      icon: ClipboardList,
      description: activePlanTitle
        ? `Active plan: ${activePlanTitle} — navigation only (owned by Care & Support).`
        : 'Navigation-only — Care Plan authoring stays with Care & Support.',
      onClick: () => openParent('care-support', 'careplans'),
      actionLabel: 'Opens Care & Support',
      isExternal: true,
    },
    {
      id: 'assessments',
      testId: 'notesdocs-card-assessments',
      title: 'Assessments',
      icon: ClipboardCheck,
      description: 'Unified read-index over Risk Assessments, Care Plan sections and assessment documents.',
      onClick: () => onOpenLeaf('assessments'),
      actionLabel: 'Open Assessments',
    },
    {
      id: 'reports',
      testId: 'notesdocs-card-reports',
      title: 'Reports',
      icon: FileBarChart2,
      description: 'Combined index — generated system reports and uploaded report documents.',
      onClick: () => onOpenLeaf('reports'),
      actionLabel: 'Open Reports',
    },
    {
      id: 'incident-forms',
      testId: 'notesdocs-card-incident-forms',
      title: 'Incident Forms',
      icon: AlertTriangle,
      description: 'Participant-filtered access to Incident Management (owned by Compliance).',
      onClick: openIncidentsExternal,
      actionLabel: 'Opens Incident Management',
      isExternal: true,
    },
    {
      id: 'consent-forms',
      testId: 'notesdocs-card-consent-forms',
      title: 'Consent Forms',
      icon: ShieldCheck,
      description: 'Dual-source labelled read view (structured consents + signed documents).',
      onClick: () => onOpenLeaf('consents'),
      actionLabel: 'Open Consent Forms',
    },
    {
      id: 'risk-assessments',
      testId: 'notesdocs-card-risk-assessments',
      title: 'Risk Assessments',
      icon: ShieldAlert,
      description: 'Navigation-only — Risk creation/scoring stays with Risk & Safeguarding.',
      onClick: () => openParent('risk', 'risks'),
      actionLabel: 'Opens Risk & Safeguarding',
      isExternal: true,
    },
    {
      id: 'upload-documents-forms',
      testId: 'notesdocs-card-upload-documents',
      title: 'Upload Documents & Forms',
      icon: Upload,
      description: 'Temporarily unavailable — the participant document upload path does not currently enforce extension↔MIME consistency or magic-byte content validation. Existing documents remain accessible via the legacy Documents leaf. A separate P0 security remediation task is required.',
      onClick: null,
      actionLabel: 'Upload unavailable — awaiting P0 security remediation',
      isExternal: false,
      isDisabled: true,
    },
    {
      id: 'document-categories',
      testId: 'notesdocs-card-document-categories',
      title: 'Document Categories',
      icon: Tags,
      description: 'Read-only reference — the 12 canonical categories used by the durable pipeline.',
      onClick: () => onOpenLeaf('document-categories'),
      actionLabel: 'Open Categories',
    },
    {
      id: 'review-expiry',
      testId: 'notesdocs-card-review-expiry',
      title: 'Review & Expiry Dates',
      icon: CalendarClock,
      description: 'Aggregated read-only index across documents, plans, consents and other authoritative dates.',
      onClick: () => onOpenLeaf('expiring'),
      actionLabel: 'Open Review & Expiry',
    },
  ];

  return (
    <div className="space-y-4" data-testid="notesdocs-landing">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const cardDisabled = !!c.isDisabled;
          return (
            <Card
              key={c.id}
              data-testid={c.testId}
              data-disabled={cardDisabled ? 'true' : undefined}
              aria-disabled={cardDisabled || undefined}
              className={
                'flex flex-col transition-all ' +
                (cardDisabled
                  ? 'opacity-60 cursor-not-allowed border-dashed'
                  : 'hover:border-primary/60 hover:shadow-sm cursor-pointer')
              }
              onClick={cardDisabled ? undefined : c.onClick}
              role={cardDisabled ? 'group' : 'button'}
              tabIndex={cardDisabled ? -1 : 0}
              onKeyDown={(e) => {
                if (cardDisabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  c.onClick();
                }
              }}
            >
              <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2 min-w-0">
                  {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
                  <CardTitle className="text-sm font-semibold truncate">{c.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-sm flex-1 flex flex-col justify-between gap-3">
                <p className="text-xs text-muted-foreground">{c.description}</p>
                <div
                  className="flex items-center justify-end text-xs font-medium"
                  data-testid={`${c.testId}-action`}
                >
                  <span className={cardDisabled ? 'text-amber-700' : 'text-primary'}>
                    {c.actionLabel}
                  </span>
                  {!cardDisabled && <ChevronRight className="w-3 h-3 ml-1" />}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      <p
        className="text-[11px] text-muted-foreground italic"
        data-testid="notesdocs-landing-footer"
      >
        Case Management Notes and Version History are deferred pending
        separate governance workstreams and are intentionally not surfaced here.
      </p>
    </div>
  );
}

export default NotesDocsLanding;
